import { useEffect, useMemo, useState } from 'react'
import { Button, Cascader, Modal, Tag, message } from 'antd'
import { saveZbPaymentReceiptsApi } from '@/services/apis/controlNodesZb'
import type { ExpenseGroup, PaymentGroup } from './data'

type LinkValue = { toContractCode: string; toCode: string }
interface Props {
  projectID: string
  expenses: ExpenseGroup[]
  payments: PaymentGroup[]
  receipts: API.ControlNodesZb.PaymentReceipt[]
  defaultCode?: string
  editable: boolean
  onClose: () => void
  onUpdate: () => void
}

// 收付关联编辑（拷贝自老页面同名弹窗的交互，接口走 zb 专属收付关联，落新表）
export default function PaymentExpenseLink({ projectID, expenses, payments, receipts, defaultCode, editable, onClose, onUpdate }: Props) {
  const [current, setCurrent] = useState<string>()
  const [draft, setDraft] = useState<Record<string, LinkValue[]>>({})
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    const next: Record<string, LinkValue[]> = {}
    expenses.forEach(line => line.nodes.forEach(n => {
      next[`${line.purContractCode}:${n.paymentplancode}`] = receipts
        .filter(r => r.fromContractCode === line.purContractCode && r.fromCode === n.paymentplancode)
        .map(r => ({ toContractCode: r.toContractCode, toCode: r.toCode }))
    }))
    setDraft(next)
    setCurrent(expenses.find(l => l.purContractCode === defaultCode)?.purContractCode || expenses[0]?.purContractCode)
  }, [expenses, receipts, defaultCode])
  const options = useMemo(() => payments.map(line => ({
    value: line.contractCode,
    label: <div className="break-all max-w-[260px] whitespace-pre-wrap">收款线-{line.contractName}</div>,
    children: line.nodes.map(n => ({ value: n.pmCode, label: <div className="break-all max-w-[260px] whitespace-pre-wrap">{n.description}</div> })),
  })), [payments])
  const labelOf = (link: LinkValue) => {
    const line = payments.find(l => l.contractCode === link.toContractCode)
    const node = line?.nodes.find(n => n.pmCode === link.toCode)
    return line && node ? `收款-${line.contractName}-${node.description}` : ''
  }
  const line = expenses.find(l => l.purContractCode === current)
  const save = async () => {
    if (busy) return
    const list: Omit<API.ControlNodesZb.PaymentReceipt, 'ID' | 'opProjectId'>[] = []
    expenses.forEach(expense => expense.nodes.forEach(n => {
      ;(draft[`${expense.purContractCode}:${n.paymentplancode}`] || []).forEach(link => list.push({ type: '', fromContractCode: expense.purContractCode, fromCode: n.paymentplancode, toContractCode: link.toContractCode, toCode: link.toCode }))
    }))
    setBusy(true)
    try { await saveZbPaymentReceiptsApi(projectID, list); onUpdate(); onClose() } catch (e) { message.error(String(e)) } finally { setBusy(false) }
  }
  return <Modal open title="收付关联" maskClosable={false} width="fit-content" style={{ minWidth: 520 }} onCancel={onClose} footer={editable ? <div className="flex justify-end gap-2"><Button onClick={onClose}>取消</Button><Button type="primary" loading={busy} onClick={save}>保存</Button></div> : <Button onClick={onClose}>关闭</Button>}>
    <div className="flex gap-1 border-b pb-3 mb-2 overflow-x-auto">
      {expenses.map(item => <button key={item.purContractCode} className={`text-xs px-4 py-2 whitespace-nowrap rounded-sm ${current === item.purContractCode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`} onClick={() => setCurrent(item.purContractCode)}>{item.purContractName}</button>)}
    </div>
    {!expenses.length && <div className="text-center text-gray-400 py-6">暂无费用线</div>}
    <table className="zb-control-table"><thead><tr>{['合同编号', '时间', '供应商名称', '付费物料', '付款条件', '关联节点'].map(h => <th key={h}>{h}</th>)}</tr></thead><tbody>
      {line?.nodes.map(n => {
        const key = `${line.purContractCode}:${n.paymentplancode}`
        const links = draft[key] || []
        return <tr key={key}>
          <td>{line.purContractCode}</td><td>{n.predictpaytime?.slice(0, 10) || '-'}</td><td>{n.supplierName}</td><td>{n.purchasematter}</td><td>{n.paymentcondition}</td>
          <td style={{ minWidth: 200 }}>{editable
            ? <Cascader className="w-full" multiple showCheckedStrategy={Cascader.SHOW_CHILD} placeholder="选择节点" options={options} value={links.map(l => [l.toContractCode, l.toCode])} onChange={paths => setDraft(d => ({ ...d, [key]: paths.map(p => ({ toContractCode: String(p[0]), toCode: String(p[1]) })) }))} />
            : <div className="flex flex-wrap gap-1">{links.map((l, i) => <Tag key={i}>{labelOf(l)}</Tag>)}</div>}</td>
        </tr>
      })}
    </tbody></table>
  </Modal>
}
